import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../../admin/api';
import { fDateTime } from '../../../utils/formatTime';
import {
  AdminDataTable,
  AdminDetailPanel,
  AdminStatusChip,
  AdminTimeline,
  AdminNoDataState,
} from '../shared';
import AdjustmentActionDialog from './AdjustmentActionDialog';
import {
  buildEventTimelineItems,
  formatAdjustmentAmount,
  formatAdjustmentType,
  formatDecimalForDisplay,
  formatValue,
  getAllowedActions,
} from './adjustmentsShared';

const DRAWER_WIDTH = 760;

function DetailLoadingState() {
  return (
    <Stack spacing={3}>
      <Skeleton variant="rounded" height={112} />
      <Skeleton variant="rounded" height={180} />
      <Skeleton variant="rounded" height={180} />
      <Skeleton variant="rounded" height={240} />
    </Stack>
  );
}

function isImageUrl(url) {
  return /\.(png|jpe?g|webp)$/i.test(url || '');
}

function AttachmentSection({ urls }) {
  if (!urls.length) {
    return (
      <AdminNoDataState
        title="No attachments"
        description="This adjustment request does not currently include any uploaded files."
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">Attachments</Typography>
      <Stack spacing={1.5}>
        {urls.map((url, index) => (
          <Stack
            key={`${url}-${index}`}
            spacing={1}
            sx={{
              p: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
            }}
          >
            <Link href={url} target="_blank" rel="noreferrer" underline="hover">
              Attachment {index + 1}
            </Link>
            {isImageUrl(url) ? (
              <Box
                component="img"
                src={url}
                alt={`Adjustment attachment ${index + 1}`}
                sx={{
                  width: '100%',
                  maxHeight: 220,
                  objectFit: 'contain',
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                }}
              />
            ) : null}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

AttachmentSection.propTypes = {
  urls: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default function AdjustmentDetailsDrawer({
  open,
  requestId,
  onClose,
  onRequestUpdated,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [detailState, setDetailState] = useState({
    loading: false,
    error: null,
    data: null,
  });
  const [dialogState, setDialogState] = useState({
    open: false,
    action: null,
    note: '',
    submitting: false,
    error: null,
  });

  const loadDetail = useCallback(async () => {
    if (!requestId) {
      return;
    }

    setDetailState({
      loading: true,
      error: null,
      data: null,
    });

    try {
      const response = await fetchAdmin(`/adjustments/${requestId}`);
      setDetailState({
        loading: false,
        error: null,
        data: response,
      });
    } catch (error) {
      setDetailState({
        loading: false,
        error: toErrorMessage(error),
        data: null,
      });
    }
  }, [requestId]);

  useEffect(() => {
    if (!open || !requestId) {
      return undefined;
    }

    loadDetail();
    return undefined;
  }, [loadDetail, open, requestId]);

  const request = detailState.data?.request || null;
  const approvals = detailState.data?.approvals || [];
  const events = detailState.data?.events;
  const actions = getAllowedActions(request?.status);

  const closeDialog = () => {
    setDialogState({
      open: false,
      action: null,
      note: '',
      submitting: false,
      error: null,
    });
  };

  const openDialog = (action) => {
    setDialogState({
      open: true,
      action,
      note: '',
      submitting: false,
      error: null,
    });
  };

  const handleSubmitAction = async () => {
    if (!request || !dialogState.action) {
      return;
    }

    setDialogState((prev) => ({
      ...prev,
      submitting: true,
      error: null,
    }));

    const payload =
      dialogState.action === 'approve' || dialogState.action === 'reject'
        ? dialogState.note.trim()
          ? { note: dialogState.note.trim() }
          : {}
        : undefined;

    try {
      const response = await postAdmin(
        `/adjustments/${request.request_id}/${dialogState.action}`,
        payload
      );

      if (dialogState.action === 'execute' && response?.reference) {
        enqueueSnackbar(`Adjustment executed. Reference: ${response.reference}`, {
          variant: 'success',
        });
      } else {
        enqueueSnackbar('Adjustment action completed successfully.', {
          variant: 'success',
        });
      }

      closeDialog();
      await Promise.all([loadDetail(), onRequestUpdated?.()]);
    } catch (error) {
      setDialogState((prev) => ({
        ...prev,
        submitting: false,
        error: toErrorMessage(error),
      }));
    }
  };

  const summaryItems = request
    ? [
        { label: 'Request ID', value: formatValue(request.request_id) },
        { label: 'Adjustment type', value: formatAdjustmentType(request.adjustment_type) },
        { label: 'Target type', value: formatValue(request.target_type) },
        { label: 'User ID', value: formatValue(request.user_id) },
        { label: 'Direction', value: formatValue(request.direction) },
        { label: 'Status', value: formatValue(request.status) },
        { label: 'Amount', value: formatAdjustmentAmount(request) },
        { label: 'Reason code', value: formatValue(request.reason_code) },
        { label: 'Reason note', value: formatValue(request.reason_note, 'No note provided') },
        { label: 'Reference type', value: formatValue(request.reference_type, 'No reference type') },
        { label: 'Reference ID', value: formatValue(request.reference_id, 'No reference ID') },
      ]
    : [];

  const workflowItems = request
    ? [
        { label: 'Approval rule', value: formatValue(request.approval_rule_code) },
        { label: 'Requested by', value: formatValue(request.requested_by) },
        { label: 'Approved by', value: formatValue(request.approved_by, 'Not approved') },
        { label: 'Executed by', value: formatValue(request.executed_by, 'Not executed') },
        { label: 'Execution transaction', value: formatValue(request.execution_transaction_id, 'Not created') },
        { label: 'Execution reference', value: formatValue(request.execution_reference, 'Not created') },
        { label: 'Created at', value: request.created_at ? fDateTime(request.created_at) : 'N/A' },
        { label: 'Approved at', value: request.approved_at ? fDateTime(request.approved_at) : 'Not approved' },
        { label: 'Executed at', value: request.executed_at ? fDateTime(request.executed_at) : 'Not executed' },
        { label: 'Cancelled at', value: request.cancelled_at ? fDateTime(request.cancelled_at) : 'Not cancelled' },
      ]
    : [];

  const balanceItems = request
    ? request.adjustment_type === 'wallet_snapshot_correction'
      ? [
          { label: 'Balance', value: formatDecimalForDisplay(request.snapshot_balance, 'NGN') },
          { label: 'Ledger balance', value: formatDecimalForDisplay(request.snapshot_ledger_balance, 'NGN') },
          { label: 'USDT balance', value: formatDecimalForDisplay(request.snapshot_usdt_balance, 'USDT') },
          { label: 'USDT ledger balance', value: formatDecimalForDisplay(request.snapshot_usdt_ledger_balance, 'USDT') },
        ]
      : [
          { label: 'Wallet type', value: formatValue(request.wallet_type) },
          { label: 'Asset code', value: formatValue(request.asset_code) },
          { label: 'Amount', value: formatAdjustmentAmount(request) },
        ]
    : [];

  const approvalRows = approvals.map((approval) => ({
    id: approval.id,
    stage: approval.approval_stage,
    staffId: approval.staff_id || 'N/A',
    decision: approval.decision || 'N/A',
    note: approval.note || 'No note',
    createdAt: approval.created_at ? fDateTime(approval.created_at) : 'N/A',
  }));

  const eventTimelineItems = useMemo(
    () => buildEventTimelineItems(events || []),
    [events]
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', lg: DRAWER_WIDTH },
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
            sx={{ px: 3, py: 2.5 }}
          >
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              <Typography variant="h6">Adjustment Detail</Typography>
              <Typography variant="body2" color="text.secondary">
                Review the full request payload, supporting evidence, approval history, and allowed state actions.
              </Typography>
              {request ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <AdminStatusChip status={request.status} />
                  <Chip label={formatAdjustmentType(request.adjustment_type)} variant="outlined" />
                  <Chip label={`Direction: ${formatValue(request.direction)}`} variant="outlined" />
                  <Chip
                    label={
                      request.image_urls?.length
                        ? `${request.image_urls.length} attachment${request.image_urls.length > 1 ? 's' : ''}`
                        : 'No attachments'
                    }
                    color={request.image_urls?.length ? 'info' : 'default'}
                    variant="outlined"
                  />
                </Stack>
              ) : null}
            </Stack>
            <IconButton onClick={onClose} aria-label="Close adjustment details">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
            {detailState.loading ? <DetailLoadingState /> : null}

            {!detailState.loading && detailState.error ? (
              <Alert severity="error" variant="outlined">
                {detailState.error}
              </Alert>
            ) : null}

            {!detailState.loading && !detailState.error && !request ? (
              <AdminNoDataState
                title="No adjustment selected"
                description="Open a request from the list to inspect the adjustment details and workflow history."
              />
            ) : null}

            {!detailState.loading && !detailState.error && request ? (
              <Stack spacing={3}>
                {actions.length ? (
                  <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                    {actions.includes('approve') ? (
                      <LoadingButton variant="contained" color="success" onClick={() => openDialog('approve')}>
                        Approve
                      </LoadingButton>
                    ) : null}
                    {actions.includes('reject') ? (
                      <Button variant="outlined" color="error" onClick={() => openDialog('reject')}>
                        Reject
                      </Button>
                    ) : null}
                    {actions.includes('execute') ? (
                      <LoadingButton variant="contained" color="primary" onClick={() => openDialog('execute')}>
                        Execute
                      </LoadingButton>
                    ) : null}
                    {actions.includes('cancel') ? (
                      <Button variant="outlined" color="error" onClick={() => openDialog('cancel')}>
                        Cancel
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}

                <Grid container spacing={3}>
                  <Grid item xs={12} xl={6}>
                    <AdminDetailPanel title="Request Summary" items={summaryItems} />
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <AdminDetailPanel title="Workflow State" items={workflowItems} />
                  </Grid>
                  <Grid item xs={12}>
                    <AdminDetailPanel
                      title={
                        request.adjustment_type === 'wallet_snapshot_correction'
                          ? 'Snapshot Values'
                          : 'Ledger Values'
                      }
                      items={balanceItems}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AttachmentSection urls={request.image_urls || []} />
                  </Grid>
                  <Grid item xs={12}>
                    <AdminDataTable
                      title="Approval History"
                      description="Approval records returned with the adjustment detail payload."
                      columns={[
                        { field: 'stage', headerName: 'Stage' },
                        { field: 'staffId', headerName: 'Staff ID' },
                        { field: 'decision', headerName: 'Decision' },
                        { field: 'note', headerName: 'Note' },
                        { field: 'createdAt', headerName: 'Created' },
                      ]}
                      rows={approvalRows}
                      emptyTitle="No approval history"
                      emptyDescription="No approval records have been returned for this request yet."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AdminTimeline
                      title="Request Events"
                      items={eventTimelineItems}
                    />
                  </Grid>
                </Grid>
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Drawer>

      <AdjustmentActionDialog
        open={dialogState.open}
        action={dialogState.action}
        request={request}
        note={dialogState.note}
        submitting={dialogState.submitting}
        error={dialogState.error}
        onClose={closeDialog}
        onNoteChange={(note) =>
          setDialogState((prev) => ({
            ...prev,
            note,
          }))
        }
        onSubmit={handleSubmitAction}
      />
    </>
  );
}

AdjustmentDetailsDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  onRequestUpdated: PropTypes.func,
  open: PropTypes.bool.isRequired,
  requestId: PropTypes.string,
};
